import React, { useEffect, useState } from 'react';
import { catalogApi } from '../utils/api';
import { toast } from '../components/Toast';
import { track, EVENTS } from '../utils/analytics';
import EmptyState from '../components/EmptyState';
import InlineLoadError from '../components/InlineLoadError';

export default function UnpricedItemsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [prices, setPrices] = useState({});
  const [saving, setSaving] = useState({});

  const rowKey = (r) => `${r.supplier_id}|${r.sku}|${r.description}`;

  const load = () => {
    setLoading(true);
    setLoadError(false);
    catalogApi.unpricedItems()
      .then(r => setRows(r.data))
      // Was a toast only, so a failed load rendered "אין פריטים ללא מחיר" —
      // telling an admin their backlog is clear when it may not be.
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const save = async (r) => {
    const key = rowKey(r);
    const price = parseFloat(prices[key]);
    if (!(price > 0)) { toast('יש להזין מחיר גדול מ-0', 'error'); return; }
    setSaving(s => ({ ...s, [key]: true }));
    try {
      const res = await catalogApi.setPriceAndFill(r.supplier_id, { sku: r.sku, name: r.description, fixed_price: price });
      const b = res.data?.backfill;
      track(EVENTS.UNPRICED_ITEM_PRICED, { lines_filled: b?.lines_filled || 0, documents_updated: b?.documents_updated || 0, has_sku: !!r.sku });
      const msg = b && (b.lines_filled || b.documents_updated)
        ? `נשמר · מולאו ${b.lines_filled} שורות ב-${b.documents_updated} תעודות`
        : 'המחיר נשמר';
      toast(msg);
      setRows(prev => prev.filter(x => rowKey(x) !== key));
    } catch (e) {
      toast('שגיאה בהזנת מחיר', 'error');
    } finally {
      setSaving(s => ({ ...s, [key]: false }));
    }
  };

  const bySupplier = rows.reduce((acc, r) => {
    (acc[r.supplier_id] = acc[r.supplier_id] || { name: r.supplier_name, items: [] }).items.push(r);
    return acc;
  }, {});

  return (
    <div>
      <h1 className="page-title">פריטים ללא מחיר</h1>
      <p style={{ color: 'var(--ink-muted)', fontSize: 14 }}>
        מוצרים שהתקבלו על תעודות משלוח/החזרה ללא מחיר, ואין להם מחיר ידוע בשום מקום.
        הזיני מחיר קבוע — הוא יישמר בקטלוג ויושלם אוטומטית על התעודות של אותו ספק.
      </p>
      {loading ? <div className="loading-container"><div className="spinner" /><span>טוען...</span></div>
        : loadError ? <InlineLoadError message="לא ניתן לטעון את הפריטים" onRetry={load} />
        : rows.length === 0 ? <EmptyState message="אין פריטים ללא מחיר" />
        : Object.entries(bySupplier).map(([sid, grp]) => (
          <div key={sid} className="card" style={{ marginTop: 18 }}>
            <h3 style={{ marginBottom: 8 }}>{grp.name}</h3>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>מוצר</th><th>מק"ט</th><th>תעודות</th><th>שורות</th><th>מחיר קבוע</th><th></th></tr>
                </thead>
                <tbody>
                  {grp.items.map(r => {
                    const key = rowKey(r);
                    return (
                      <tr key={key}>
                        <td>{r.description || '—'}</td>
                        <td>{r.sku || '—'}</td>
                        <td>{r.doc_count}</td>
                        <td>{r.line_count}</td>
                        <td>
                          <input type="number" min="0" step="0.01" style={{ width: 90 }}
                            value={prices[key] ?? ''}
                            onChange={e => setPrices(p => ({ ...p, [key]: e.target.value }))}
                            onKeyDown={e => { if (e.key === 'Enter') save(r); }} />
                        </td>
                        <td>
                          <button className="btn btn-primary btn-sm" disabled={saving[key]} onClick={() => save(r)}>
                            {saving[key] ? '…' : 'שמור והשלם'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}
    </div>
  );
}
